// Blueprint v1 (ADR-001 mục 1): JSON có `schemaVersion`, mọi thực thể có id ổn định có tiền tố (hoặc `tmp_*` khi mới). Đây là
// dạng người / template / (sau) AI viết; runtime KHÔNG đọc dạng này — chỉ đọc manifest đã biên dịch (mục 9).
import { z } from "zod";
import { I18nLabel } from "../dynamic-data.ts";
import { ID_PREFIXES, type IdKind } from "./ids.ts";

export const BLUEPRINT_SCHEMA_VERSION = 1;
export const BLUEPRINT_LIMITS = { collections: 50, fieldsPerCollection: 100, optionsPerField: 200, views: 200, policies: 200, scopeTypes: 50 } as const;

/** Id thật `<tiền tố>_<ULID>` của đúng loại, hoặc `tmp_*` (thực thể mới, engine cấp id thật khi apply). */
export const BlueprintId = (kind: IdKind) => z.string().regex(new RegExp(`^(${ID_PREFIXES[kind]}_[0-7][0-9A-HJKMNP-TV-Z]{25}|tmp_[a-z0-9_]{1,64})$`), `id ${ID_PREFIXES[kind]}_… hoặc tmp_…`);
const Key = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/, "chữ thường, số, gạch dưới; bắt đầu bằng chữ");
const OptionKey = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/);
export const LifecycleStatus = z.enum(["ACTIVE", "RETIRED"]);

export const BlueprintOption = z.object({ id: BlueprintId("option"), key: OptionKey, label: I18nLabel, status: LifecycleStatus.default("ACTIVE") }).strict();

const fieldBase = { id: BlueprintId("field"), key: Key, label: I18nLabel, required: z.boolean().default(false), indexed: z.boolean().default(false), status: LifecycleStatus.default("ACTIVE") };
export const BlueprintField = z.discriminatedUnion("type", [
  z.object({ ...fieldBase, type: z.enum(["text", "number", "date", "bool"]) }).strict(),
  /** K5: loại tiền cố định trong field; `minorUnitScale` lấy từ sổ `currencies` lúc biên dịch, không do người viết khai. */
  z.object({ ...fieldBase, type: z.literal("money"), currency: z.string().regex(/^[A-Z]{3}$/) }).strict(),
  z.object({ ...fieldBase, type: z.literal("select"), options: z.array(BlueprintOption).min(1).max(BLUEPRINT_LIMITS.optionsPerField) }).strict(),
  z.object({ ...fieldBase, type: z.literal("relation"), targetCollectionId: BlueprintId("collection"), cardinality: z.literal("one").default("one") }).strict(),
]);
export type BlueprintField = z.infer<typeof BlueprintField>;

export const BlueprintCollection = z
  .object({ id: BlueprintId("collection"), key: Key, label: I18nLabel, status: LifecycleStatus.default("ACTIVE"), fields: z.array(BlueprintField).max(BLUEPRINT_LIMITS.fieldsPerCollection) })
  .strict();
export type BlueprintCollection = z.infer<typeof BlueprintCollection>;

/** `filter`: SecretAAExpression với `{ field: "fld_…" }` — validator đổi sang uuid rồi kiểm bằng validateExpression. */
export const BlueprintView = z
  .object({
    id: BlueprintId("view"),
    collectionId: BlueprintId("collection"),
    key: Key,
    label: I18nLabel,
    kind: z.enum(["table", "form", "detail"]),
    fields: z.array(BlueprintId("field")).max(BLUEPRINT_LIMITS.fieldsPerCollection),
    filter: z.unknown().optional(),
    sort: z.object({ fieldId: z.union([BlueprintId("field"), z.literal("createdAt")]), dir: z.enum(["asc", "desc"]) }).strict().optional(),
  })
  .strict();
export type BlueprintView = z.infer<typeof BlueprintView>;

/** Policy chỉ THU HẸP trong vai trò project (ADR-010): không có dòng nào ⇒ vai trò giữ đủ quyền mặc định của nó. */
export const BlueprintPermission = z.enum(["read", "create", "update", "retire"]);
export const BlueprintPolicy = z
  .object({ id: BlueprintId("policy"), collectionId: BlueprintId("collection"), role: z.enum(["viewer", "editor", "manager"]), allow: z.array(BlueprintPermission).max(4) })
  .strict();
export type BlueprintPolicy = z.infer<typeof BlueprintPolicy>;

export const BlueprintScopeType = z.object({ id: BlueprintId("scopeType"), key: z.string().regex(/^[a-z][a-z0-9_]{1,31}$/), label: I18nLabel }).strict();

export const BlueprintSpec = z
  .object({
    schemaVersion: z.literal(BLUEPRINT_SCHEMA_VERSION),
    collections: z.array(BlueprintCollection).max(BLUEPRINT_LIMITS.collections).default([]),
    views: z.array(BlueprintView).max(BLUEPRINT_LIMITS.views).default([]),
    policies: z.array(BlueprintPolicy).max(BLUEPRINT_LIMITS.policies).default([]),
    scopeTypes: z.array(BlueprintScopeType).max(BLUEPRINT_LIMITS.scopeTypes).default([]),
    /** Giữ chỗ (ADR-001 mục 1): khác rỗng ⇒ BLUEPRINT_SECTION_NOT_SUPPORTED ở v1. */
    tasks: z.array(z.unknown()).default([]),
    flows: z.array(z.unknown()).default([]),
    toolBindings: z.array(z.unknown()).default([]),
  })
  .strict();
export type BlueprintSpec = z.infer<typeof BlueprintSpec>;
export type BlueprintSpecInput = z.input<typeof BlueprintSpec>;

/** JSON Schema cho import / export và (sau) structured output của AI MVP. */
export const blueprintJsonSchema = () => z.toJSONSchema(BlueprintSpec, { io: "input" });
